import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageIcon, CalendarDays, MapPin, Upload, Trash2, X } from "lucide-react";
import { toast } from "sonner";
function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Gallery() {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const isCoach = user?.role === "coach" || user?.role === "admin";
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [pendingFile, setPendingFile] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/photos");
      setPhotos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Photo trop volumineuse (max 6 Mo)");
      return;
    }
    setPendingFile(file);
    setCaption("");
  };

  const confirmUpload = async () => {
    if (!pendingFile) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", pendingFile);
      form.append("caption", caption || "");
      const res = await fetch(`${API}/gallery`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload échoué");
      }
      toast.success("Photo ajoutée à la galerie");
      setPendingFile(null);
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setUploading(false);
    }
  };

  const cancelUpload = () => {
    setPendingFile(null);
    setCaption("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteGalleryPhoto = async (id) => {
    if (!confirm("Supprimer cette photo ?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Photo supprimée");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-slate-500 mb-2">
            Mur du club
          </div>
          <h1 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight flex items-center gap-3">
            <ImageIcon className="w-8 h-8 sm:w-10 sm:h-10 text-[#E5B85C]" />
            Galerie souvenirs
          </h1>
        </div>
        {isCoach && !pendingFile && (
          <label
            data-testid="gallery-upload-button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] font-bold uppercase tracking-wide text-sm cursor-pointer whitespace-nowrap"
          >
            <Upload className="w-4 h-4" /> Ajouter une photo
            <input
              ref={fileRef}
              data-testid="gallery-file-input"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickFile}
            />
          </label>
        )}
      </div>

      {pendingFile && (
        <div
          data-testid="gallery-upload-modal"
          className="rounded-2xl bg-slate-900 border border-[#E5B85C]/30 p-5 space-y-4"
        >
          <div className="flex items-center justify-between">
            <div className="font-display font-bold uppercase tracking-wide text-sm">
              Nouvelle photo · {pendingFile.name}
            </div>
            <button
              data-testid="gallery-cancel-upload"
              onClick={cancelUpload}
              className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <input
            data-testid="gallery-caption-input"
            type="text"
            maxLength={200}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Légende (facultative)"
            className="w-full px-3 py-2.5 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={cancelUpload}
              className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs uppercase tracking-wide font-bold"
            >
              Annuler
            </button>
            <button
              data-testid="gallery-confirm-upload"
              onClick={confirmUpload}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-xs font-bold uppercase tracking-wide disabled:opacity-60"
            >
              <Upload className="w-3.5 h-3.5" />
              {uploading ? "Envoi..." : "Publier"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : photos.length === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
          Aucune photo souvenir pour l'instant
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {photos.map((p) =>
            p.kind === "gallery" ? (
              <GalleryStandaloneCard
                key={`g-${p.id}`}
                photo={p}
                isCoach={isCoach}
                onDelete={() => deleteGalleryPhoto(p.id)}
              />
            ) : (
              <GalleryMatchCard
                key={`m-${p.match_id}-${p.created_at}`}
                photo={p}
              />
            )
          )}
        </div>
      )}
    </div>
  );
}

function useAuthedBlob(url) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let localUrl = null;
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!alive) return;
        localUrl = URL.createObjectURL(blob);
        setSrc(localUrl);
      } catch (e) {}
    })();
    return () => {
      alive = false;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [url]);
  return src;
}

function GalleryMatchCard({ photo }) {
  const src = useAuthedBlob(`${API}/matches/${photo.match_id}/photo`);
  return (
    <Link
      to={`/matches/${photo.match_id}`}
      data-testid={`gallery-card-${photo.match_id}`}
      className="group rounded-xl bg-slate-900 border border-white/10 hover:border-[#E5B85C]/50 overflow-hidden block"
    >
      <div className="aspect-video bg-slate-800 overflow-hidden">
        {src ? (
          <img
            src={src}
            alt={photo.match_title}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-slate-700" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="font-display font-bold uppercase text-sm truncate">
          {photo.match_title}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400 font-mono-stat mt-1">
          <span className="flex items-center gap-1">
            <CalendarDays className="w-3 h-3" /> {formatDate(photo.match_date)}
          </span>
          {photo.location && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3" />
              {photo.location}
            </span>
          )}
        </div>
        {photo.match_type === "gala" && photo.score_team_a != null && (
          <div className="mt-2 flex items-center gap-2 text-sm font-mono-stat font-bold">
            <span className="text-purple-300">{photo.team_a_name || "Équipe A"} {photo.score_team_a}</span>
            <span className="text-slate-500">-</span>
            <span className="text-fuchsia-300">{photo.score_team_b} {photo.team_b_name || "Équipe B"}</span>
          </div>
        )}
        {photo.match_type === "official" && photo.our_score != null && (
          <div className="mt-2 flex items-center gap-2 text-sm font-mono-stat font-bold">
            <span className="text-[#E5B85C]">Nous {photo.our_score}</span>
            <span className="text-slate-500">-</span>
            <span className="text-slate-300">{photo.opponent_score} {photo.opponent_name}</span>
          </div>
        )}
        {photo.score_bleus != null && photo.match_type !== "gala" && photo.match_type !== "official" && (
          <div className="mt-2 flex items-center gap-2 text-sm font-mono-stat font-bold">
            <span className="text-sky-400">Bleus {photo.score_bleus}</span>
            <span className="text-slate-500">-</span>
            <span className="text-rose-400">{photo.score_rouges} Rouges</span>
          </div>
        )}
      </div>
    </Link>
  );
}

function GalleryStandaloneCard({ photo, isCoach, onDelete }) {
  const src = useAuthedBlob(`${API}/gallery/${photo.id}/image`);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);
  return (
    <div
      data-testid={`gallery-standalone-${photo.id}`}
      className="group rounded-xl bg-slate-900 border border-white/10 hover:border-[#E5B85C]/50 overflow-hidden block relative"
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`gallery-standalone-open-${photo.id}`}
        className="block w-full text-left"
        aria-label="Ouvrir en grand"
      >
        <div className="aspect-video bg-slate-800 overflow-hidden">
          {src ? (
            <img
              src={src}
              alt={photo.caption || "Photo galerie"}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-10 h-10 text-slate-700" />
            </div>
          )}
        </div>
        <div className="p-4">
          <div className="font-display font-bold uppercase text-sm truncate">
            {photo.caption || "Souvenir du club"}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-400 font-mono-stat mt-1">
            <span className="flex items-center gap-1">
              <CalendarDays className="w-3 h-3" /> {formatDate(photo.created_at)}
            </span>
            {photo.uploaded_by_name && (
              <span className="truncate">par {photo.uploaded_by_name}</span>
            )}
          </div>
        </div>
      </button>
      {isCoach && (
        <button
          data-testid={`gallery-delete-${photo.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Supprimer"
          className="absolute top-2 right-2 p-1.5 rounded-lg bg-slate-900/85 hover:bg-rose-500/40 text-slate-200 hover:text-rose-100 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      {open && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[70] bg-black/95 backdrop-blur-sm flex flex-col"
          onClick={() => setOpen(false)}
        >
          <div className="flex justify-end p-3">
            <button
              data-testid="gallery-lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-2 sm:px-8 pb-6">
            {src && (
              <img
                src={src}
                alt={photo.caption || "Photo"}
                data-testid="gallery-lightbox-image"
                className="max-w-full max-h-full object-contain rounded-lg select-none"
                draggable="false"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
          {photo.caption && (
            <div className="px-6 pb-4 text-center text-slate-300 text-sm">{photo.caption}</div>
          )}
        </div>
      )}
    </div>
  );
}
